import { jsx, jsxs } from 'react/jsx-runtime';
import React__default from 'react';
import { styled } from 'styled-components';
import { AnonBottomBarSignup } from '../ExpandedResource/ExtraPanel/AnonBottomBarSignup.js';
import { AnalyticsEvents } from '../../../analytics/analytics.types.js';
import { useAnalytics } from '../../../analytics/hooks/useAnalytics.js';
import { useMutationCreateComment } from '../../../comments/mutations/useMutationCreateComment.js';
import { useMutationUpdateComment } from '../../../comments/mutations/useMutationUpdateComment.js';
import { useQueryUserInfo } from '../../../user/queries/useQueryUserInfo.js';
import { useRunOnce } from '@fabric-app/hooks/useRunOnce';
import { framerAnimationSlideUp } from '@fabric-app/ui/constants/framerAnimations';
import { Conversation } from '@fabric-app/ui/components/Conversation';
import { ForcedTheme } from '@fabric-app/ui/theme/ForcedTheme';
import { useResourceDataContextSafe } from '../context/resourceDataContext.js';
import { IconSidePanel20Close, IconNavigational24Tick, IconNavigational24UpArrow } from '@fabric-app/icons/svg';
import { useExpandedFdocContext } from './ExpandedFdocProvider.js';
import { ButtonIcon } from '@fabric-app/ui/components/Button';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { getAnnotationText } from '../../../comments/queries/getAnnotationText.js';
import { useFabricEvent, dispatchFabricEvent } from '../../../hooks/useFabricEvent.js';

const ExpandedFdocSidebarAddComment = ({ resourceId, editingComment, onCancelEditingComment, canAutoFocus = true }) => {
  const { track } = useAnalytics();
  const { annotationData, setAnnotationData } = useExpandedFdocContext();
  const { disabledActions } = useResourceDataContextSafe();
  const { data: user } = useQueryUserInfo();
  const [textAreaRef, setTextAreaRef] = React__default.useState(null);
  const [content, setContent] = React__default.useState("");
  const wasEditingComment = React__default.useRef(Boolean(editingComment && !annotationData));
  React__default.useEffect(() => {
    if (editingComment && !annotationData) {
      setContent(editingComment.content);
      setTimeout(() => {
        textAreaRef?.focus();
      }, 200);
    } else if (wasEditingComment.current) {
      setContent("");
      textAreaRef?.blur();
    }
    wasEditingComment.current = Boolean(editingComment && !annotationData);
  }, [
    editingComment,
    annotationData,
    textAreaRef
  ]);
  const handleTextAreaChange = (e) => {
    setContent(e.target.value);
  };
  const onCancelAttachment = () => {
    onCancelEditingComment();
    if (annotationData) dispatchFabricEvent("fabric-annotation-action-delete", {
      type: "delete-annotation",
      annotationId: annotationData.id,
      resourceId
    });
    setAnnotationData(null);
  };
  const mutationCreateComment = useMutationCreateComment();
  const mutationUpdateComment = useMutationUpdateComment();
  const handleSubmit = () => {
    const trimmed = content.trim();
    if (trimmed && user) {
      setContent("");
      if (editingComment && !annotationData) {
        mutationUpdateComment.mutate({
          comment: editingComment,
          newContent: trimmed
        }, {
          onSuccess: () => {
            onCancelEditingComment();
          },
          onError: () => {
            setContent(trimmed);
          }
        });
      } else {
        mutationCreateComment.mutate({
          resourceId,
          content: trimmed,
          annotation: annotationData ?? void 0
        }, {
          onSuccess: () => {
            track(AnalyticsEvents.CreatedComment, {
              action: "expanded_fdoc"
            });
            setAnnotationData(null);
            onCancelEditingComment();
          },
          onError: () => {
            setContent(trimmed);
          }
        });
      }
    }
  };
  const handleTextAreaKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      handleSubmit();
      e.preventDefault();
    }
  };
  useRunOnce(() => {
    if (!canAutoFocus) return;
    window.requestAnimationFrame(() => {
      textAreaRef?.focus();
    });
  }, {
    condition: Boolean(textAreaRef)
  });
  const attachmentBoxText = getAnnotationText(annotationData) ?? (editingComment ? (editingComment.content.length === 0 && editingComment.annotation ? getAnnotationText(editingComment.annotation) : null) ?? {
    text: "Editing comment",
    quoted: false
  } : null);
  useFabricEvent("fabric-annotation-action-create", (e) => {
    if (e.detail.resourceId !== resourceId) return;
    setTimeout(() => {
      textAreaRef?.focus();
    }, 100);
  });
  if (disabledActions.addOrEditOwnComment) {
    return /* @__PURE__ */ jsx(AnonBottomBarSignup, {
      children: /* @__PURE__ */ jsx("p", {
        children: disabledActions.addOrEditOwnComment
      })
    });
  }
  return /* @__PURE__ */ jsxs(Conversation.Form, {
    ...framerAnimationSlideUp,
    children: [
      attachmentBoxText && !mutationUpdateComment.isPending && !mutationCreateComment.isPending && /* @__PURE__ */ jsx(CommentAttachmentBoxWrapper, {
        children: /* @__PURE__ */ jsxs(CommentAttachmentBox, {
          children: [
            /* @__PURE__ */ jsx(ButtonIcon, {
              variant: "transparent",
              size: "sm-",
              onClick: onCancelAttachment,
              children: /* @__PURE__ */ jsx(IconSidePanel20Close, {})
            }),
            /* @__PURE__ */ jsxs(CommentAttachmentBoxText, {
              children: [
                attachmentBoxText.quoted && /* @__PURE__ */ jsx("span", {
                  "data-quote": true,
                  children: "“"
                }),
                /* @__PURE__ */ jsx("span", {
                  children: attachmentBoxText.text
                }),
                attachmentBoxText.quoted && /* @__PURE__ */ jsx("span", {
                  "data-quote": true,
                  children: "”"
                })
              ]
            })
          ]
        })
      }),
      /* @__PURE__ */ jsx(ForcedTheme, {
        colorScheme: "light",
        asChild: true,
        children: /* @__PURE__ */ jsxs(Conversation.FormField, {
          children: [
            /* @__PURE__ */ jsx(Conversation.FormTextArea, {
              placeholder: "Add a comment",
              value: content,
              onChange: handleTextAreaChange,
              onKeyDown: handleTextAreaKeyDown,
              ref: setTextAreaRef,
              autoFocus: canAutoFocus,
              rows: 1,
              "data-testid": "expanded-fdoc-comment-input"
            }),
            /* @__PURE__ */ jsx(Conversation.FormButton, {
              "aria-label": "Add a comment",
              title: "Add a comment",
              disabled: !content || content.trim().length === 0,
              onClick: handleSubmit,
              "data-testid": "expanded-fdoc-comment-submit",
              children: editingComment ? /* @__PURE__ */ jsx(IconNavigational24Tick, {}) : /* @__PURE__ */ jsx(IconNavigational24UpArrow, {})
            })
          ]
        })
      })
    ]
  });
};
const CommentAttachmentBoxWrapper = styled.div.withConfig({
  displayName: "ExpandedFdocSidebar_AddComment__CommentAttachmentBoxWrapper",
  componentId: "sc-be105dcd-0"
})([
  `height:24px;width:100%;`
]);
const CommentAttachmentBoxText = styled.span.withConfig({
  displayName: "ExpandedFdocSidebar_AddComment__CommentAttachmentBoxText",
  componentId: "sc-be105dcd-1"
})([
  `display:flex;align-items:center;min-width:0;overflow:hidden;> span{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;&[data-quote]{flex-shrink:0;}}background:`,
  `;color:`,
  `;font-size:14px;font-weight:400;`
], cssVar["color-bg-contrast"], cssVar["color-text-tertiary"]);
const CommentAttachmentBox = styled.div.withConfig({
  displayName: "ExpandedFdocSidebar_AddComment__CommentAttachmentBox",
  componentId: "sc-be105dcd-2"
})([
  `position:absolute;top:-2px;left:0;width:100%;z-index:2;display:flex;gap:0.75rem;height:36px;align-items:center;border-bottom:2px solid `,
  `;padding:0 1rem;background:`,
  `;color:`,
  `;font-size:14px;font-weight:400;`
], cssVar["color-border-secondary"], cssVar["color-bg-contrast"], cssVar["color-text-tertiary"]);

export { ExpandedFdocSidebarAddComment };
//# sourceMappingURL=ExpandedFdocSidebar_AddComment.js.map
